import React from "react";
import { api, videoUrl, videoPosterUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Trash2, UploadCloud, Play } from "lucide-react";

export default function AdminVideos() {
  const [videos, setVideos] = React.useState([]);
  const [file, setFile] = React.useState(null);
  const [poster, setPoster] = React.useState(null);
  const [caption, setCaption] = React.useState("");
  const [order, setOrder] = React.useState(0);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  const load = () => api.get("/videos").then((r) => setVideos(r.data || []));
  React.useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a video file");
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (poster) fd.append("poster", poster);
      fd.append("caption", caption);
      fd.append("order", String(order));
      await api.post("/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
        timeout: 600000,
      });
      toast.success("Video uploaded");
      setFile(null);
      setPoster(null);
      setCaption("");
      setOrder(0);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/videos/${id}`);
    toast.success("Deleted");
    load();
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  return (
    <div data-testid="admin-videos-page">
      <div className="mb-8">
        <p className="overline mb-2">Visual Library</p>
        <h1 className="font-serif-display text-4xl">Videos</h1>
        <p className="text-[#A1A4B5] text-sm mt-2 max-w-xl">
          Upload up to 200&nbsp;MB per video (MP4 / MOV / WebM). An optional poster image gives the player a polished thumbnail.
        </p>
      </div>

      <form
        onSubmit={upload}
        className="border border-[var(--border)] rounded-2xl p-6 mb-10 bg-[#13151D]"
        data-testid="video-upload-form"
      >
        <h3 className="font-serif-display text-xl mb-4">Upload New Video</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Video File</Label>
            <Input
              type="file"
              accept="video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="video-file-input"
            />
            {file && (
              <p className="text-[11px] text-[#A1A4B5] mt-1.5">
                {file.name} · {formatSize(file.size)}
              </p>
            )}
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Poster (optional)</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setPoster(e.target.files?.[0] || null)}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="video-poster-input"
            />
            {poster && (
              <p className="text-[11px] text-[#A1A4B5] mt-1.5">{poster.name}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Caption</Label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              placeholder="e.g. Sunset poolside walkthrough"
              data-testid="video-caption-input"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Order</Label>
            <Input
              type="number"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="video-order-input"
            />
          </div>
        </div>

        {uploading && progress > 0 && (
          <div className="mt-5">
            <div className="h-1.5 w-full bg-[#0A0B10] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#C5A059] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[11px] text-[#A1A4B5] mt-2">Uploading… {progress}%</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={uploading}
          className="gold-btn rounded-full mt-6 px-6"
          data-testid="video-upload-submit"
        >
          <UploadCloud size={16} className="mr-2" />
          {uploading ? "Uploading…" : "Upload Video"}
        </Button>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.length === 0 && (
          <p className="text-[#A1A4B5] col-span-full">No videos yet — upload your first walkthrough.</p>
        )}
        {videos.map((v) => (
          <div
            key={v.id}
            className="relative group rounded-xl overflow-hidden border border-[var(--border)] bg-[#13151D]"
            data-testid={`video-card-${v.id}`}
          >
            <div className="relative aspect-video">
              {v.poster_path ? (
                <img
                  src={videoPosterUrl(v.id)}
                  alt={v.caption}
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  src={videoUrl(v.id)}
                  preload="metadata"
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 grid place-items-center bg-black/40">
                <div className="w-12 h-12 rounded-full bg-[#C5A059]/90 grid place-items-center">
                  <Play size={18} fill="#0A0B10" stroke="#0A0B10" />
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="text-sm text-white truncate">{v.caption || v.original_filename}</div>
              <div className="text-[11px] text-[#A1A4B5] mt-1">
                {formatSize(v.size_bytes)} · order {v.order}
              </div>
              <Button
                size="sm"
                onClick={() => remove(v.id)}
                className="mt-3 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40"
                data-testid={`delete-video-${v.id}`}
              >
                <Trash2 size={12} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
